import * as R from 'ramda'
import { of, EMPTY } from 'rxjs'
import { catchError, concatMap, delayWhen, filter, map, startWith, switchMap } from 'rxjs/operators'
import { ofType } from 'redux-observable'
import * as RE from 'helpers/ramda-ext'
import { pickersCall } from 'api/planet/pickers'
import { DEFAULT_PARTICIPANTS_VALUES, DEFAULT_ADULTS_PARTICIPANTS, PARTICIPANTS_CATEGORIES } from 'constants/participants'
import {
  getNewParticipantsSelection,
  removeParticipantsBirthdates
} from 'domain/participants'
import { getFirstParticipantsPossibility } from 'domain/pricerParticipants'
import { captureException } from 'helpers/raven'
import * as Actions from 'store/actions/index.ts'
import {
  getActivePackage,
  getActivePackageKey,
  getCountryCode,
  getCultureCode,
  getCurrentProductKey,
  getCurrentSelection,
  getDepartureCityAutoSelect,
  getDepartureDateAutoSelect,
  getDurationAutoSelect,
  getHasVisibleDepartureCityPicker,
  getHasVisibleDurationPicker,
  getHasVisibleParticipantsPicker,
  getIsActivePackageFetched,
  getIsCurrentProductOutOfStock,
  getIsProductPagePath,
  getMarketId,
  getMaxPax,
  getMinPax,
  getMinEqualsMaxPax,
  getParticipantsFromLS,
  getParticipantsRules,
  getParticipantsSettings,
  getSaleKey,
  getSelectedParticipants,
  getSelectedCheckout,
  getShouldDoubleThePriceFeatureEnabled
} from 'store/selectors'
import { handleUnauthorized } from './helpers'
import { getBenefitsAutoSelect } from '../selectors/pricerBenefits'

export const fetchCheckinDatesEpic = (actions$, state$) => actions$.pipe(
  ofType(
    Actions.configurator.CONFIGURATOR_SELECTION_CHANGE,
    Actions.package.SELECT_PACKAGE,
    Actions.resources.RESOURCE_FETCHED
  ),
  filter(action => action.type === Actions.resources.RESOURCE_FETCHED ? action.payload.type === 'package' : true),
  concatMap(action => {
    const state = state$.value
    const currentSelection = getCurrentSelection(state)
    const hasVisibleDepartureCityPicker = getHasVisibleDepartureCityPicker(state)
    const hasVisibleDurationPicker = getHasVisibleDurationPicker(state)

    const isConfiguratorSelectionChange = action.type === Actions.configurator.CONFIGURATOR_SELECTION_CHANGE

    const hasDepartureCity = !hasVisibleDepartureCityPicker || (currentSelection && Boolean(currentSelection.departureCity))
    const hasDuration = !hasVisibleDurationPicker || (currentSelection && RE.isNotNil(currentSelection.nights))

    const hasDepartureCityAndDurationInCurrentSelection = (
      currentSelection &&
      hasDepartureCity &&
      hasDuration
    )

    const hasNewDepartureCityAndDurationInCurrentSelection = (
      isConfiguratorSelectionChange &&
      Boolean(action.payload.selection.departureCity) &&
      hasDuration
    )

    const hasNewDurationAndDepartureCityInCurrentSelection = (
      isConfiguratorSelectionChange &&
      hasDepartureCity &&
      RE.isNotNil(action.payload.selection.nights)
    )

    if ((
      action.type === Actions.resources.RESOURCE_FETCHED &&
      !hasVisibleDepartureCityPicker &&
      !hasVisibleDurationPicker
    ) || (
      action.type === Actions.package.SELECT_PACKAGE &&
      hasDepartureCityAndDurationInCurrentSelection
    ) || (
      isConfiguratorSelectionChange && (
        hasNewDepartureCityAndDurationInCurrentSelection ||
        hasNewDurationAndDepartureCityInCurrentSelection
      )
    )) {
      const countryCode = getCountryCode(state)
      const cultureCode = getCultureCode(state)
      const marketId = getMarketId(state)
      const packageKey = getActivePackageKey(state)
      const productKey = getCurrentProductKey(state)
      const saleKey = getSaleKey(state)
      const isResourceV2 = getShouldDoubleThePriceFeatureEnabled(state)
      const version = state.member?.version

      return pickersCall(
        countryCode,
        cultureCode,
        currentSelection && currentSelection.departureCity,
        marketId,
        hasVisibleDurationPicker && currentSelection ? currentSelection.nights : null,
        packageKey,
        productKey,
        saleKey,
        version,
        isResourceV2
      ).pipe(
        switchMap(({ response }) => of(Actions.configurator.onPackageElementsFetched(packageKey, response ?? { departureDates: [] }))),
        startWith(Actions.fetch.onFetchStarted()),
        catchError(error => {
          if (typeof error.status === 'number') {
            if (error.status === 0) {
              return of(Actions.errors.onNetworkError(error))
            }

            if (error.status === 401) {
              return handleUnauthorized()
            }
          }
          captureException(new Error('Unable to fetch resource'), {
            extra: {
              countryCode,
              cultureCode,
              currentSelection,
              marketId,
              packageKey,
              productKey,
              saleKey
            }
          })
          return of(Actions.configurator.onPackageElementsFetchError(packageKey))
        })
      )
    }

    return EMPTY
  })
)

export const getProcessedParticipants = (action, state) => {
  const hasVisiblePicker = getHasVisibleParticipantsPicker(state)

  if (!hasVisiblePicker) {
    return null
  }

  const currentSelection = getCurrentSelection(state)

  const isParticipantsSourceFromLS = (!currentSelection || action.type === Actions.package.SELECT_PACKAGE)
  const minEqualsMax = getMinEqualsMaxPax(state)
  const maxPax = getMaxPax(state)
  const minPax = getMinPax(state)
  const shouldUseMinPax = minPax > DEFAULT_ADULTS_PARTICIPANTS
  const adultParticipantsNumber = shouldUseMinPax ? minPax : DEFAULT_ADULTS_PARTICIPANTS
  const defaultParticipantsConf = {
    ...DEFAULT_PARTICIPANTS_VALUES,
    adults: {
      birthdates: [],
      number: adultParticipantsNumber
    }
  }
  const participantsFromCS = getSelectedParticipants(state)
  const participantsFromLS = shouldUseMinPax
    ? defaultParticipantsConf
    : getParticipantsFromLS(state)
  const participantsRules = getParticipantsRules(state)
  const participantsSource = isParticipantsSourceFromLS ? participantsFromLS : participantsFromCS
  const settings = getParticipantsSettings(state)

  if (!participantsSource && minEqualsMax) {
    return defaultParticipantsConf
  } else if (participantsSource) {
    const checkoutDate = getSelectedCheckout(state)
    const participants = participantsSource
    const updatedParticipantsSelection = getNewParticipantsSelection(
      participants,
      settings,
      { checkoutDate, shouldUpdateSelection: !isParticipantsSourceFromLS }
    )
    const isUpdatedParticipantsSelectionValid = (updatedParticipantsSelection !== participants)
    const sumOfParticipants = PARTICIPANTS_CATEGORIES.reduce((sum, category) => sum + participants[category].number, 0)

    if (isParticipantsSourceFromLS) {
      return (
        (isUpdatedParticipantsSelectionValid)
          ? updatedParticipantsSelection
          : getFirstParticipantsPossibility(settings.conf, undefined, minEqualsMax, participantsRules)
      )
    } else if (isUpdatedParticipantsSelectionValid) {
      const hasParticipantsSelectionChanged = !R.equals(updatedParticipantsSelection, participants)

      return (
        (hasParticipantsSelectionChanged)
          ? updatedParticipantsSelection
          : null
      )
    } else if (sumOfParticipants < minPax || sumOfParticipants > maxPax) {
      return defaultParticipantsConf
    }

    return removeParticipantsBirthdates(participants)
  }

  return getFirstParticipantsPossibility(settings.conf, undefined, minEqualsMax, participantsRules)
}

export const configuratorAutoSelectEpic = (actions$, state$) => actions$.pipe(
  ofType(
    Actions.configurator.CONFIGURATOR_SELECTION_CHANGE,
    Actions.configurator.PACKAGE_ELEMENTS_FETCHED,
    Actions.package.SELECT_PACKAGE
  ),
  delayWhen(() => {
    if (getIsActivePackageFetched(state$.value)) return of({})

    return actions$.pipe(
      ofType(Actions.resources.RESOURCE_FETCHED),
      filter(R.pipe(
        R.path(['payload', 'type']),
        R.allPass([
          R.equals('package'),
          () => getIsActivePackageFetched(state$.value),
          () => getIsProductPagePath(state$.value)
        ])
      ))
    )
  }),
  map(action => {
    const state = state$.value

    if (Actions.configurator.CONFIGURATOR_SELECTION_CHANGE === action.type && !action.payload.shouldAutoSelect) { return {} }

    if (getIsCurrentProductOutOfStock(state)) { return {} }
    if (!getActivePackage(state)) { return {} }

    const departureCity = getDepartureCityAutoSelect(state)
    const nights = getDurationAutoSelect(state)
    const checkin = getDepartureDateAutoSelect(state)
    const participants = getProcessedParticipants(action, state)
    const benefits = getBenefitsAutoSelect(state)

    const autoSelection = {
      benefits,
      checkin,
      departureCity,
      nights,
      participants
    }

    return RE.rejectNil(autoSelection)
  }
  ),
  filter(R.complement(R.isEmpty)),
  map(selection => (
    Actions.configurator.onConfiguratorSelectionChange(getActivePackage(state$.value).key, selection, true)
  ))
)
